import React, { createContext, useState, useContext, useEffect } from 'react';
import { clearThemeChoice, explicitTheme, rememberThemeChoice, systemPrefersDark, themeIsDark } from './shared/themePreference';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [choice, setChoice] = useState(() => explicitTheme(localStorage));
  const [isDark, setIsDark] = useState(() => themeIsDark(
    localStorage,
    typeof window !== 'undefined' ? window.matchMedia : null
  ));

  useEffect(() => {
    const page = isDark ? '#1a1a1a' : '#FFFFFF';
    document.documentElement.style.colorScheme = isDark ? 'dark' : 'light';
    document.documentElement.style.backgroundColor = page;
    document.body.style.backgroundColor = page;
  }, [isDark]);

  useEffect(() => {
    if (choice) return undefined;
    if (typeof window.matchMedia !== 'function') return undefined;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => setIsDark(media.matches === true);
    apply();
    if (media.addEventListener) media.addEventListener('change', apply);
    else if (media.addListener) media.addListener(apply);
    return () => {
      if (media.removeEventListener) media.removeEventListener('change', apply);
      else if (media.removeListener) media.removeListener(apply);
    };
  }, [choice]);

  const applyAppearance = (next) => {
    if (next === 'system') {
      clearThemeChoice(localStorage);
      setChoice(null);
      setIsDark(systemPrefersDark(typeof window !== 'undefined' ? window.matchMedia : null));
      return;
    }
    const dark = next === 'dark';
    rememberThemeChoice(localStorage, dark);
    setChoice(dark ? 'dark' : 'light');
    setIsDark(dark);
  };

  const toggleTheme = () => {
    applyAppearance(isDark ? 'light' : 'dark');
  };

  const theme = {
    isDark,
    colors: {
      // Backgrounds
      background: isDark ? '#1a1a1a' : '#FFFFFF',
      surface: isDark ? '#2a2a2a' : '#FFFFFF',
      surfaceAlt: isDark ? '#333333' : '#f9fafb',

      // Text
      text: isDark ? '#f9fafb' : '#1a1a1a',
      textSecondary: isDark ? '#d1d5db' : '#666',
      textMuted: isDark ? '#9ca3af' : '#999',

      // Borders
      border: isDark ? '#404040' : '#f0f0f0',
      borderLight: isDark ? '#333333' : '#e5e7eb',

      // Primary (Teal)
      primary: '#59c4c4',
      primaryHover: '#4ab3b3',
      primaryLight: isDark ? 'rgba(89, 196, 196, 0.2)' : 'rgba(89, 196, 196, 0.1)',

      // Status colors
      success: '#10b981',
      warning: '#f59e0b',
      error: '#59c4c4',

      // Shadows
      shadow: isDark ? 'rgba(0, 0, 0, 0.5)' : 'rgba(0, 0, 0, 0.08)',
      shadowLarge: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.15)',
    }
  };

  return (
    <ThemeContext.Provider value={{ ...theme, toggleTheme, appearance: choice || 'system', setAppearance: applyAppearance }}>
      {children}
    </ThemeContext.Provider>
  );
};
